import { useState } from 'react';
import FlowStepper from './FlowStepper';
import PlacementPreview from './PlacementPreview';
import SimulatedCheckout from './SimulatedCheckout';
import {
  TOURNAMENT_PROMOTIONS,
  STATE_GROUPS,
  REGION_BY_ID,
  promotionById,
  formatUSD,
  PLATFORM_FEE_RATE,
  REGISTRATION_CONCEPT,
} from './revenueData';
import type { Region } from './revenueData';

const STEPS = ['Promotion', 'Regions', 'Placement', 'Checkout', 'Activation'];

const fieldClass = 'w-full text-sm border border-background-300 rounded-md px-3.5 py-2.5 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClass = 'block font-label uppercase tracking-widest text-[11px] text-foreground-600 mb-1.5';

export default function TournamentWorkflow() {
  const [step, setStep] = useState(0);
  const [promotionId, setPromotionId] = useState<string | null>(null);
  const [regionIds, setRegionIds] = useState<string[]>([]);
  const [done, setDone] = useState(false);
  const [calc, setCalc] = useState({ individuals: 24, foursomes: 6, individualPrice: 150, foursomePrice: 520 });

  const promo = promotionId ? promotionById(promotionId) : undefined;
  const regionLabels = regionIds.map((id) => `${REGION_BY_ID[id].city}, ${REGION_BY_ID[id].state === 'California' ? 'CA' : 'AZ'}`);

  const gross = calc.individuals * calc.individualPrice + calc.foursomes * calc.foursomePrice;
  const fee = Math.round(gross * PLATFORM_FEE_RATE);

  const toggleRegion = (id: string) => setRegionIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const go = (i: number) => setStep(Math.max(0, Math.min(STEPS.length - 1, i)));
  const restart = () => { setStep(0); setPromotionId(null); setRegionIds([]); setDone(false); };

  const canNext = step === 0 ? Boolean(promotionId) : step === 1 ? regionIds.length > 0 : true;
  const amountLabel = promo?.price === null ? '3% fee' : formatUSD(promo?.price || 0);

  return (
    <div className="space-y-6">
      <FlowStepper steps={STEPS} current={step} label="Tournament organizer workflow" onJump={go} />

      {step === 0 && (
        <section>
          <h3 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">Choose a tournament promotion</h3>
          <p className="mt-2 text-foreground-600 text-sm max-w-2xl">Organizers can list for free, then pay to boost visibility. An optional managed registration service is also available. All prices are illustrative.</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-5">
            {TOURNAMENT_PROMOTIONS.map((p) => {
              const active = promotionId === p.id;
              return (
                <button key={p.id} type="button" onClick={() => setPromotionId(p.id)} className={`text-left rounded-lg border p-5 cursor-pointer transition-colors ${active ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-400' : p.featured ? 'border-accent-300 bg-background-50 hover:border-accent-500' : 'border-background-200 bg-background-50 hover:border-primary-400'}`}>
                  <span className="w-10 h-10 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center text-xl"><i className="ri-trophy-line"></i></span>
                  <h4 className="mt-4 font-heading font-bold text-lg text-foreground-950 leading-tight">{p.name}</h4>
                  <p className="mt-1 font-label uppercase tracking-widest text-[10px] text-foreground-500">{p.tagline}</p>
                  <p className="mt-3 font-heading font-extrabold text-2xl text-foreground-950">{p.price === null ? '3%' : formatUSD(p.price)}<span className="text-xs font-body font-normal text-foreground-500"> {p.duration}</span></p>
                  <ul className="mt-4 space-y-2">
                    {p.perks.map((perk) => (
                      <li key={perk} className="flex items-start gap-2 text-xs text-foreground-600">
                        <span className="w-4 h-4 flex items-center justify-center text-accent-700 shrink-0 mt-0.5"><i className="ri-check-line"></i></span>{perk}
                      </li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {step === 1 && (
        <section>
          <h3 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">Choose regions for this promotion</h3>
          <p className="mt-2 text-foreground-600 text-sm max-w-2xl">Where should this tournament be promoted? Larger promotions can span multiple markets.</p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {STATE_GROUPS.map((group) => (
              <div key={group.state} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <p className="font-label uppercase tracking-widest text-[11px] font-bold text-foreground-700">{group.state}</p>
                <div className="mt-3 flex flex-wrap gap-2.5">
                  {group.regions.map((r: Region) => {
                    const on = regionIds.includes(r.id);
                    return (
                      <button key={r.id} type="button" onClick={() => toggleRegion(r.id)} className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full border font-label font-semibold text-xs whitespace-nowrap cursor-pointer transition-colors ${on ? 'bg-primary-500 border-primary-500 text-background-50' : 'bg-background-100 border-background-200 text-foreground-700 hover:border-primary-400'}`}>
                        <span className="w-4 h-4 flex items-center justify-center">{on ? <i className="ri-checkbox-circle-line"></i> : <i className="ri-add-circle-line"></i>}</span>
                        {r.city}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {step === 2 && (
        <PlacementPreview
          brand="Sample Charity Scramble"
          tagline="Four-person scramble to benefit the local junior golf fund."
          regions={regionLabels}
          packageName={promo?.name || 'Promotion'}
          kind="tournament"
        />
      )}

      {step === 3 && (
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <SimulatedCheckout
              amountLabel={amountLabel}
              summaryLine={`${promo?.name} · ${regionIds.length} region${regionIds.length === 1 ? '' : 's'}`}
              done={done}
              onSimulate={() => { setDone(true); setStep(4); }}
            />
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <p className="font-label uppercase tracking-widest text-[11px] font-bold text-foreground-700">Note</p>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">No player information or payment details are ever collected in this demo. Registration payments and payouts are simulated only.</p>
            </div>
          </div>
        </section>
      )}

      {step === 4 && (
        <section className="space-y-6">
          <div className="rounded-lg border border-primary-200 bg-primary-50 p-5 md:p-6">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-md bg-primary-500 text-background-50 flex items-center justify-center text-2xl"><i className="ri-rocket-2-line"></i></span>
              <div>
                <h3 className="font-heading font-bold text-2xl text-foreground-950">Promotion scheduled &mdash; simulated</h3>
                <p className="text-sm text-foreground-600">{promo?.name} · {regionLabels.length ? regionLabels.join(', ') : '—'}</p>
              </div>
            </div>
            <p className="mt-4 text-sm text-foreground-700">In the live product this promotion would be scheduled and paid for. Nothing here is published, charged or saved.</p>
          </div>
          <button type="button" onClick={restart} className="inline-flex items-center gap-2 border border-background-300 hover:border-foreground-700 text-foreground-700 font-label font-bold uppercase tracking-wider text-xs px-4 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-restart-line"></i></span>
            Restart tournament demo
          </button>
        </section>
      )}

      {/* Registration revenue calculator */}
      <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center text-lg"><i className="ri-calculator-line"></i></span>
            <h3 className="font-heading font-bold text-xl text-foreground-950">Registration revenue calculator</h3>
          </div>
          <span className="bg-accent-100 text-accent-900 border border-accent-300 font-label font-bold uppercase tracking-widest text-[10px] px-2.5 py-1 rounded-sm whitespace-nowrap">Sample figures</span>
        </div>
        <p className="mt-2 text-sm text-foreground-600">With the optional managed registration service, the platform handles sign-ups for an illustrative {Math.round(PLATFORM_FEE_RATE * 100)}% fee. Adjust the sample entries to see the fee.</p>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="tw-ind" className={labelClass}>Individual entries</label>
                <input id="tw-ind" type="number" min={0} value={calc.individuals} onChange={(e) => setCalc((p) => ({ ...p, individuals: Math.max(0, Number(e.target.value) || 0) }))} className={fieldClass} />
              </div>
              <div>
                <label htmlFor="tw-indp" className={labelClass}>Individual price ($)</label>
                <input id="tw-indp" type="number" min={0} value={calc.individualPrice} onChange={(e) => setCalc((p) => ({ ...p, individualPrice: Math.max(0, Number(e.target.value) || 0) }))} className={fieldClass} />
              </div>
              <div>
                <label htmlFor="tw-four" className={labelClass}>Foursome entries</label>
                <input id="tw-four" type="number" min={0} value={calc.foursomes} onChange={(e) => setCalc((p) => ({ ...p, foursomes: Math.max(0, Number(e.target.value) || 0) }))} className={fieldClass} />
              </div>
              <div>
                <label htmlFor="tw-fourp" className={labelClass}>Foursome price ($)</label>
                <input id="tw-fourp" type="number" min={0} value={calc.foursomePrice} onChange={(e) => setCalc((p) => ({ ...p, foursomePrice: Math.max(0, Number(e.target.value) || 0) }))} className={fieldClass} />
              </div>
            </div>
            <div className="mt-4 text-xs text-foreground-500 space-y-1.5">
              <p className="flex items-center gap-2"><span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-line"></i></span>{REGISTRATION_CONCEPT.individual.label} example: {formatUSD(REGISTRATION_CONCEPT.individual.example)}</p>
              <p className="flex items-center gap-2"><span className="w-4 h-4 flex items-center justify-center"><i className="ri-group-line"></i></span>{REGISTRATION_CONCEPT.foursome.label} example: {formatUSD(REGISTRATION_CONCEPT.foursome.example)} (saves {formatUSD(REGISTRATION_CONCEPT.foursome.saving)})</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <p className="font-label uppercase tracking-widest text-[10px] text-foreground-500">Gross registrations</p>
                <p className="mt-1 font-heading font-extrabold text-2xl text-foreground-950">{formatUSD(gross)}</p>
              </div>
              <div>
                <p className="font-label uppercase tracking-widest text-[10px] text-foreground-500">Platform fee (3%)</p>
                <p className="mt-1 font-heading font-extrabold text-2xl text-accent-700">{formatUSD(fee)}</p>
              </div>
              <div>
                <p className="font-label uppercase tracking-widest text-[10px] text-foreground-500">Payout to organizer</p>
                <p className="mt-1 font-heading font-extrabold text-2xl text-foreground-950">{formatUSD(gross - fee)}</p>
              </div>
            </div>
            <p className="mt-3 text-xs text-foreground-500 flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-information-line"></i></span>
              Formula: (individuals × price) + (foursomes × price) = gross; platform fee = 3% of gross; payout = gross − fee.
            </p>
          </div>
        </div>
      </section>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <button type="button" onClick={() => go(step - 1)} disabled={step === 0} className="inline-flex items-center gap-2 border border-background-300 hover:border-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed text-foreground-700 font-label font-bold uppercase tracking-wider text-xs px-4 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors">
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
          Back
        </button>
        {step < 3 && (
          <button type="button" onClick={() => go(step + 1)} disabled={!canNext} className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed text-background-50 font-label font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors">
            Continue
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
          </button>
        )}
      </div>
    </div>
  );
}